import { ArrowUp, Paperclip } from "lucide-react"
import { useEffect, useRef, useState } from "react"

import { AttachmentChip } from "@/components/ask/attachment-chip"
import { Button } from "@/components/ui/button"

interface ChatInputProps {
  onSubmit: (message: string, files?: File[]) => void
  disabled?: boolean
  placeholder?: string
}

export function ChatInput({
  onSubmit,
  disabled = false,
  placeholder = "Ask me anything or describe what you need...",
}: ChatInputProps) {
  const [message, setMessage] = useState("")
  const [files, setFiles] = useState<File[]>([])
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = "auto"
    el.style.height = `${el.scrollHeight}px`
  }, [message])

  function handleSubmit() {
    const trimmed = message.trim()
    if ((!trimmed && files.length === 0) || disabled) return
    onSubmit(trimmed, files.length > 0 ? files : undefined)
    setMessage("")
    setFiles([])
  }

  function handleAttachClick() {
    const input = document.createElement("input")
    input.type = "file"
    input.accept = ".pdf,.png,.jpg,.jpeg"
    input.multiple = true
    input.style.display = "none"
    document.body.appendChild(input)
    input.addEventListener("change", () => {
      const selected = input.files
      if (selected && selected.length > 0) {
        setFiles((prev) => [...prev, ...Array.from(selected)])
      }
      input.remove()
    })
    input.click()
  }

  function removeFile(index: number) {
    setFiles((prev) => prev.filter((_, i) => i !== index))
  }

  return (
    <div className="border-input bg-background w-full overflow-hidden rounded-2xl border shadow-sm">
      {files.length > 0 && (
        <div className="flex flex-col gap-2 px-3 pt-3">
          {files.map((file, index) => (
            <AttachmentChip
              key={`${file.name}-${index}`}
              filename={file.name}
              fileSize={file.size}
              contentType={file.type}
              onRemove={() => removeFile(index)}
            />
          ))}
        </div>
      )}

      <textarea
        ref={textareaRef}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault()
            handleSubmit()
          }
        }}
        placeholder={placeholder}
        disabled={disabled}
        rows={1}
        className="placeholder:text-muted-foreground mt-3 mb-2 max-h-40 w-full resize-none overflow-y-auto border-0 bg-transparent px-4 py-0 text-[15px] outline-none focus:ring-0"
      />

      <div className="flex items-center justify-between px-3 pb-3">
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          disabled={disabled}
          onClick={handleAttachClick}
          className="cursor-pointer rounded-lg"
        >
          <Paperclip className="size-4" />
        </Button>

        <Button
          size="icon-sm"
          disabled={(!message.trim() && files.length === 0) || disabled}
          onClick={handleSubmit}
          className="cursor-pointer rounded-lg"
        >
          <ArrowUp className="size-4" />
        </Button>
      </div>
    </div>
  )
}
